
// ─── src/components/dashboard/ApplicantDetailModal.tsx ────────────────────────
import { useEffect, type FC } from "react";
import { X, Lightbulb, ExternalLink, CheckCircle, XCircle, Accessibility } from "lucide-react";
import type { Registration } from "../../types/registration";
import { Badge } from "../ui/Badge";
import {
  avatarColor, initials, capitalize, formatDate,
  STATUS_BADGE, defaultStatusBadge,
} from "../../utils/formatter";

const APP_STATUS_BADGE: Record<string, string> = {
  pending:  "bg-amber-500/20 text-amber-300 border border-amber-500/30",
  approved: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30",
  rejected: "bg-red-500/20 text-red-300 border border-red-500/30",
};

interface Props {
  reg: Registration;
  onClose: () => void;
}

const Field: FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
  <div>
    <p className="text-xs text-brand-muted mb-0.5">{label}</p>
    <p className="text-white text-sm">{value ?? "—"}</p>
  </div>
);

const BoolField: FC<{ label: string; value: boolean }> = ({ label, value }) => (
  <div className="flex items-center gap-2">
    {value
      ? <CheckCircle size={16} className="text-emerald-400 shrink-0" />
      : <XCircle    size={16} className="text-brand-muted/50 shrink-0" />}
    <span className={`text-sm ${value ? "text-white" : "text-brand-muted"}`}>
      {label}
    </span>
  </div>
);

export const ApplicantDetailModal: FC<Props> = ({ reg, onClose }) => {
  // Close on Esc
  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4
                 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="card w-full max-w-2xl max-h-[90vh] overflow-y-auto
                      shadow-2xl shadow-black/40 animate-in fade-in-0
                      slide-in-from-bottom-4 duration-200">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-brand-border">
          <div className="flex items-center gap-4">
            <div
              className={`${avatarColor(reg.fullName)} w-14 h-14 rounded-2xl
                          flex items-center justify-center text-white
                          text-lg font-bold shrink-0`}
            >
              {initials(reg.fullName)}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-bold text-white">{reg.fullName}</h2>
                {reg.status && (
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                                ${APP_STATUS_BADGE[reg.status] ?? "bg-gray-500/20 text-gray-300 border border-gray-500/30"}`}
                  >
                    {reg.status}
                  </span>
                )}
              </div>
              <p className="text-brand-muted text-sm">{reg.email}</p>
              {reg.createdAt && (
                <p className="text-brand-muted text-xs mt-0.5">
                  Registered {formatDate(reg.createdAt)}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-brand-muted hover:text-white
                       hover:bg-brand-border transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Personal */}
          <Section title="Personal Information">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Field label="Phone"    value={reg.phone} />
              <Field label="Gender"   value={capitalize(reg.gender)} />
              <Field label="Age"      value={reg.age} />
              <Field label="City"     value={reg.city} />
            </div>
          </Section>

          {/* Professional */}
          <Section title="Professional Background">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Field label="Primary Role"       value={capitalize(reg.primaryRole)} />
              <Field label="Years of Experience" value={`${reg.yearsOfExperience} years`} />
              <div>
                <p className="text-xs text-brand-muted mb-1">Current Status</p>
                <Badge
                  label={capitalize(reg.currentStatus)}
                  className={STATUS_BADGE[reg.currentStatus] ?? defaultStatusBadge}
                />
              </div>
            </div>
            {reg.linkedInUrl && (
              <a
                href={reg.linkedInUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-brand-orange
                           hover:text-brand-orangeHover text-sm mt-2 transition-colors"
              >
                LinkedIn Profile <ExternalLink size={13} />
              </a>
            )}
          </Section>

          {/* Idea */}
          {reg.isPitchingIdea && (
            <Section title="Startup Idea" icon={<Lightbulb size={16} className="text-brand-orange" />}>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Idea Title"    value={reg.ideaTitle} />
                  <Field label="Idea Theme"    value={reg.ideaTheme} />
                </div>
                <Field label="Problem Solving" value={reg.problemSolving} />
                <Field label="Target Users"    value={reg.targetUsers} />
                <Field label="Current Stage"   value={reg.currentStage} />
              </div>
            </Section>
          )}

          {/* Event Details */}
          <Section title="Event Details">
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <BoolField label="Available all days"          value={reg.availableAllDays} />
                <BoolField label="Pitching an idea"            value={reg.isPitchingIdea} />
                <BoolField label="Requesting scholarship"      value={reg.requestingScholarship ?? false} />
                <BoolField label="Accepted terms & conditions" value={reg.acceptedTerms} />
              </div>
              {reg.whyThisEvent && (
                <Field label="Why this event" value={reg.whyThisEvent} />
              )}
              {reg.requestingScholarship && reg.whyScholarship && (
                <Field label="Why scholarship" value={reg.whyScholarship} />
              )}
            </div>
          </Section>

          {/* Accessibility — only shown when the participant shared something */}
          {reg.accessibilityNeeds && (
            <Section
              title="Accessibility & Special Needs"
              icon={<Accessibility size={16} className="text-blue-400" />}
            >
              <div className="flex gap-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <Accessibility size={16} className="text-blue-400 shrink-0 mt-0.5" />
                <p className="text-sm text-white leading-relaxed">{reg.accessibilityNeeds}</p>
              </div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
};

// tiny helper
const Section: FC<{
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, children }) => (
  <div>
    <h3 className="flex items-center gap-2 text-xs font-semibold uppercase
                   tracking-wider text-brand-muted mb-3">
      {icon}{title}
    </h3>
    <div className="bg-brand-navy/50 rounded-xl p-4">{children}</div>
  </div>
);